import { Toast as BaseToast } from '@base-ui/react/toast'
import { ToastAction } from './action'
import { ToastDescription } from './description'
import { ToastIcon } from './icon'
import { ToastRoot } from './root'
import { isToastTone } from './tones'
import type { ToastData } from './use-toast'
import { ToastViewport } from './viewport'
import type { PartProps } from '../../../lib/part-props'

interface ToastProviderProps extends PartProps<BaseToast.Provider.Props> {
  /**
   * Where the toasts mount. Left out, they pin to the bottom centre of the window. Given an element (or `null`
   * while it mounts), they sit in that element's flow instead, so the host positions them.
   */
  container?: BaseToast.Portal.Props['container']
  /** The ways a toast swipes away. Defaults to down or right; `[]` turns swiping off. */
  swipeDirection?: BaseToast.Root.Props['swipeDirection']
}

// In a host's element the viewport gives up its window-pinned position and takes the host's width.
const IN_FLOW_VIEWPORT = 'static z-auto [&>*]:max-w-full'

function ToastList({ swipeDirection }: Pick<ToastProviderProps, 'swipeDirection'>) {
  const { toasts } = BaseToast.useToastManager<ToastData>()
  // Newest first; the front is the newest one not on its way out.
  const front = toasts.find((toast) => toast.transitionStatus !== 'ending' && !toast.limited)
  return toasts.map((toast) => {
    const tone = isToastTone(toast.type) ? toast.type : undefined
    const hasIcon = tone !== undefined || toast.data?.icon !== undefined
    const isFront = toast.id === front?.id
    return (
      <ToastRoot
        key={toast.id}
        toast={toast}
        swipeDirection={swipeDirection}
        covered={front !== undefined && !isFront && toast.transitionStatus !== 'ending'}
      >
        {hasIcon ? <ToastIcon tone={tone}>{toast.data?.icon}</ToastIcon> : null}
        <ToastDescription />
        <ToastAction />
      </ToastRoot>
    )
  })
}

/**
 * Wrap the app once. Renders the portal, the live-region viewport and the toasts itself. One shows at
 * a time; up to three wait underneath on their own timers, each gone after five seconds, all paused on hover.
 */
function ToastProvider({ container, swipeDirection, children, ...props }: ToastProviderProps) {
  return (
    <BaseToast.Provider {...props}>
      {children}
      <BaseToast.Portal container={container}>
        <ToastViewport className={container === undefined ? undefined : IN_FLOW_VIEWPORT}>
          <ToastList swipeDirection={swipeDirection} />
        </ToastViewport>
      </BaseToast.Portal>
    </BaseToast.Provider>
  )
}

export { ToastProvider }
